/**
 * PlacementTableView - Compact table display for AutoPark results
 *
 * Linear/Stripe-inspired table view optimized for quick scanning and searching.
 * Shows aircraft placements in a sortable, filterable table format.
 */

import {
  Box,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import { Shape } from "../../../Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import {
  Placement,
  ParkingRequest,
  Spot,
  Zone,
} from "../../../Ramp/TemporalTest/types";
import {
  PartialPlacementInfo,
} from "../utils/detectPartialPlacements";
import { ScheduleChange } from "./SpotOccupancyGantt";
import {
  buildTableRows,
  filterBySearch,
  filterByStatus,
  sortRows,
  TableRow as PlacementRow,
} from "../utils/buildTableRows";
import { PlacementTableFilters } from "./PlacementTableFilters";
import { PlacementTableSummary } from "./PlacementTableSummary";
import { PlacementTableRow } from "./PlacementTableRow";

interface PlacementTableViewProps {
  // Data
  statistics?: {
    totalAircraft: number;
    placedAircraft: number;
    unplacedAircraft: number;
    partiallyPlacedAircraft?: number;
    averageMoves?: number;
  };
  placement: Placement;
  requests: ParkingRequest[];
  spots: Spot[];
  zones?: Zone[];
  rampShapes: Shape[];
  startTime: Date;
  horizonHours: number;
  partialPlacements: PartialPlacementInfo[];

  // Callbacks
  onScheduleChanged?: (changes: ScheduleChange[]) => void;

  // Config
  enableEditing?: boolean;
}

type SortColumn = 'status' | 'tail' | 'spot' | 'arrival' | 'departure';
type SortOrder = 'asc' | 'desc';

export const PlacementTableView: React.FC<PlacementTableViewProps> = ({
  statistics,
  placement,
  requests,
  spots,
  zones = [],
  rampShapes,
  startTime,
  horizonHours,
  partialPlacements,
  onScheduleChanged,
  enableEditing = false,
}) => {
  const theme = useTheme();

  // Filter/sort state
  const [filterMode, setFilterMode] = useState<'all' | 'placed' | 'unplaced'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortColumn>('status');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Expanded rows state
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());

  // Create spot ID to name mapping
  const spotIdToName = useMemo(() => {
    const mapping: Record<string, string> = {};
    rampShapes.forEach((shape) => {
      if (shape.type === "Spot" && shape.text) {
        mapping[shape.id] = shape.text;
      }
    });
    return mapping;
  }, [rampShapes]);

  // Build table rows
  const baseRows = useMemo(
    () => buildTableRows(requests, placement, startTime, partialPlacements, spotIdToName),
    [requests, placement, startTime, partialPlacements, spotIdToName]
  );

  // Apply filters and sorting
  const filteredRows = useMemo(() => {
    let rows = filterByStatus(baseRows, filterMode);
    rows = filterBySearch(rows, searchQuery);
    rows = sortRows(rows, sortBy, sortOrder);
    return rows;
  }, [baseRows, filterMode, searchQuery, sortBy, sortOrder]);

  // Handle sort column click
  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      // Toggle order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column - default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Handle row expansion
  const handleRowClick = (aircraftId: string) => {
    setExpandedRowIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(aircraftId)) {
        newSet.delete(aircraftId);
      } else {
        newSet.add(aircraftId);
      }
      return newSet;
    });
  };

  // Calculate filter counts
  const filterCounts = useMemo(() => {
    const all = baseRows.length;
    const placed = baseRows.filter(r => r.status === 'placed' || r.status === 'partial').length;
    const unplaced = baseRows.filter(r => r.status === 'unplaced').length;
    return { all, placed, unplaced };
  }, [baseRows]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Filter Bar */}
      <PlacementTableFilters
        filterMode={filterMode}
        onFilterModeChange={setFilterMode}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSort}
        filterCounts={filterCounts}
      />

      {/* Table Content - Grid-based */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
        }}
      >
        {/* Grid Header - Sticky */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '100px 180px 1fr 130px 130px 90px',
            gap: 2,
            alignItems: 'center',
            height: 40,
            px: 2,
            py: 1,
            bgcolor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            position: 'sticky',
            top: 0,
            zIndex: 5,
          }}
        >
          <Typography onClick={() => handleSort('status')} sx={{ fontSize: '11px', fontWeight: 600, color: 'grey.600', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer' }}>
            STATUS {sortBy === 'status' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
          </Typography>
          <Typography onClick={() => handleSort('tail')} sx={{ fontSize: '11px', fontWeight: 600, color: 'grey.600', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer' }}>
            TAIL # {sortBy === 'tail' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
          </Typography>
          <Typography onClick={() => handleSort('spot')} sx={{ fontSize: '11px', fontWeight: 600, color: 'grey.600', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer' }}>
            SPOT(S) {sortBy === 'spot' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
          </Typography>
          <Typography onClick={() => handleSort('arrival')} sx={{ fontSize: '11px', fontWeight: 600, color: 'grey.600', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer' }}>
            ARRIVAL {sortBy === 'arrival' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
          </Typography>
          <Typography onClick={() => handleSort('departure')} sx={{ fontSize: '11px', fontWeight: 600, color: 'grey.600', textTransform: 'uppercase', letterSpacing: '0.5px', cursor: 'pointer' }}>
            DEPARTURE {sortBy === 'departure' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
          </Typography>
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'grey.600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            DWELL
          </Typography>
        </Box>

        {/* Data Rows */}
        {filteredRows.map((row) => (
          <PlacementTableRow
            key={row.aircraftId}
            row={row}
            isExpanded={expandedRowIds.has(row.aircraftId)}
            onRowClick={handleRowClick}
            enableEditing={enableEditing}
            onScheduleChanged={onScheduleChanged}
            spots={spots}
            zones={zones}
            spotIdToName={spotIdToName}
          />
        ))}
      </Box>

      {/* Summary Bar */}
      <PlacementTableSummary
        totalCount={baseRows.length}
        filteredCount={filteredRows.length}
        placedCount={filterCounts.placed}
        unplacedCount={filterCounts.unplaced}
      />
    </Box>
  );
};
